/**
 * 未出貨訂單-客人 Back-Order report (S11.1).
 *
 * THE shared-selector report: its 待出量 figure comes from
 * `selectBackOrderTotal` → `selectOrderBalanceTotal` (src/lib/occ/selectors.ts)
 * over the SAME projected lens rows the OCC board reads (`useOccLines`), so
 * `OCC 待出量 == BO report figure` by construction (plan §S11.1 accept +
 * reportsBackOrder.test.ts).
 *
 * Recognition cutoff is the configurable `isRecognizedBO(line, cutoffDate)`
 * predicate; the cutoff RULE is pending 林芊, so a visible caveat banner marks
 * the report provisional and — until a date is chosen — every open-balance
 * line is recognized. BO price lock: the SO line's transacted unit price is
 * shown locked (價格鎖定); a vendor price rise is handled by the guided
 * split-to-new-SO action (the SO modify flow, drilled to here).
 */

import { useMemo, useState } from 'react';
import { Alert, Card, Col, DatePicker, Row, Statistic, Table, Tooltip, theme } from 'antd';
import type { TableProps } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import { formatAmount, formatMoney } from '@/components/salesOrders/components/soFormat';
import { controlHeightTokens } from '@/styles/tokens';
import { useOccLines, useOccNodeConfigs } from '@/hooks/useOcc';
import type { OccLensLine } from '@/lib/occ/selectors';
import type { FulfillmentNodeCode } from '@/types/api/occ';
import { useReportSalesOrders } from '../reportsData';
import { selectBackOrderLines, selectBackOrderTotal } from '../backOrderSelectors';
import { ReportSyncNote, ReportToolbar } from '../reportsShared';
import { exportReportCsv } from '../reportsCsv';

interface PriceLock {
  unitPrice: number;
  currency: string;
}

const BackOrderReport = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['reports', 'common']);
  const { token } = theme.useToken();
  const { lines, syncedAt, isLoading, error, refresh } = useOccLines();
  const { nodeLabel } = useOccNodeConfigs();
  const { rows: salesOrders } = useReportSalesOrders();

  // Cutoff rule pending 林芊 → null = recognize every open-balance line.
  const [cutoff, setCutoff] = useState<Dayjs | null>(null);
  const cutoffDate = cutoff ? cutoff.endOf('day').toISOString() : null;

  const recognized = useMemo(
    () => selectBackOrderLines(lines, cutoffDate),
    [lines, cutoffDate]
  );
  const balanceTotal = useMemo(
    () => selectBackOrderTotal(lines, cutoffDate),
    [lines, cutoffDate]
  );
  const distinctSoCount = useMemo(
    () => new Set(recognized.map((line) => line.soId)).size,
    [recognized]
  );

  /** BO price lock — the SO line's transacted price (snapshot), by soLineId. */
  const priceBySoLine = useMemo(() => {
    const map = new Map<string, PriceLock>();
    salesOrders.forEach((so) => {
      so.lines.forEach((line) => {
        map.set(line.id, { unitPrice: line.unitPrice, currency: line.currency });
      });
    });
    return map;
  }, [salesOrders]);

  const priceLockOf = (line: OccLensLine): PriceLock | undefined =>
    priceBySoLine.get(line.soLineId);

  const columns: TableProps<OccLensLine>['columns'] = [
    {
      title: t('reports:reports.backOrder.columns.so'),
      key: 'so',
      width: 150,
      fixed: 'left',
      render: (_: unknown, line) => (
        <a
          style={{ color: token.colorPrimary, cursor: 'pointer', fontWeight: token.fontWeightStrong }}
          onClick={() => navigate(`/auth/sales-orders/${line.soId}`)}
        >
          {line.soNumber}
        </a>
      ),
    },
    {
      title: t('reports:reports.backOrder.columns.customer'),
      dataIndex: 'customerName',
      key: 'customerName',
      width: 200,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('reports:reports.backOrder.columns.sku'),
      dataIndex: 'sku',
      key: 'sku',
      width: 140,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('reports:reports.backOrder.columns.product'),
      dataIndex: 'productName',
      key: 'productName',
      width: 220,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('reports:reports.backOrder.columns.node'),
      dataIndex: 'node',
      key: 'node',
      width: 130,
      render: (node: FulfillmentNodeCode) => renderDash(nodeLabel(node)),
    },
    {
      title: t('reports:reports.backOrder.columns.qtyOrdered'),
      dataIndex: 'qtyOrdered',
      key: 'qtyOrdered',
      width: 100,
      align: 'right',
      render: (value: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>,
    },
    {
      title: t('reports:reports.backOrder.columns.orderBalance'),
      dataIndex: 'qtyOpenToLoad',
      key: 'qtyOpenToLoad',
      width: 110,
      align: 'right',
      render: (value: number) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: token.fontWeightStrong }}>
          {value}
        </span>
      ),
    },
    {
      title: (
        <span>
          <LockOutlined style={{ marginInlineEnd: 4, color: token.colorTextTertiary }} />
          {t('reports:reports.backOrder.columns.lockedPrice')}
        </span>
      ),
      key: 'lockedPrice',
      width: 150,
      align: 'right',
      render: (_: unknown, line) => {
        const lock = priceLockOf(line);
        if (!lock) return '-';
        return (
          <Tooltip title={t('reports:reports.backOrder.priceLockHint')}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatMoney(lock.currency, lock.unitPrice)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: t('reports:reports.backOrder.columns.vendor'),
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 160,
      render: (value: string | null) => renderDash(value),
    },
    {
      title: t('reports:reports.backOrder.columns.action'),
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_: unknown, line) => (
        <Tooltip title={t('reports:reports.backOrder.splitHint')}>
          <a
            style={{ color: token.colorPrimary, cursor: 'pointer' }}
            onClick={() => navigate(`/auth/sales-orders/${line.soId}`)}
          >
            {t('reports:reports.backOrder.splitAction')}
          </a>
        </Tooltip>
      ),
    },
  ];

  const handleExport = () => {
    exportReportCsv<OccLensLine>(
      'back-order-customer.csv',
      [
        { header: t('reports:reports.backOrder.columns.so'), value: (line) => line.soNumber },
        { header: t('reports:reports.backOrder.columns.customer'), value: (line) => line.customerName },
        { header: t('reports:reports.backOrder.columns.sku'), value: (line) => line.sku },
        { header: t('reports:reports.backOrder.columns.product'), value: (line) => line.productName },
        { header: t('reports:reports.backOrder.columns.node'), value: (line) => nodeLabel(line.node) },
        { header: t('reports:reports.backOrder.columns.qtyOrdered'), value: (line) => line.qtyOrdered },
        { header: t('reports:reports.backOrder.columns.orderBalance'), value: (line) => line.qtyOpenToLoad },
        { header: t('reports:reports.backOrder.columns.lockedPrice'), value: (line) => {
          const lock = priceLockOf(line);
          return lock ? formatAmount(lock.unitPrice) : '';
        } },
        { header: t('reports:reports.backOrder.columns.vendor'), value: (line) => line.vendorName ?? '' },
      ],
      recognized
    );
  };

  return (
    <>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: token.marginMD }}
        message={t('reports:reports.backOrder.caveat.title')}
        description={t('reports:reports.backOrder.caveat.body')}
      />

      <ReportToolbar
        formula={t('reports:reports.backOrder.formula')}
        onExport={handleExport}
        exportDisabled={recognized.length === 0}
      />

      <Row gutter={[12, 12]} style={{ marginBottom: token.marginMD }} align="bottom">
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title={t('reports:reports.backOrder.stats.orderBalance')}
              value={balanceTotal}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title={t('reports:reports.backOrder.stats.lines')}
              value={recognized.length}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title={t('reports:reports.backOrder.stats.orders')}
              value={distinctSoCount}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <div style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary, marginBottom: token.marginXXS }}>
            {t('reports:reports.backOrder.cutoffLabel')}
          </div>
          <DatePicker
            size="large"
            style={{ width: '100%', height: `${controlHeightTokens.controlHeightXL}px` }}
            value={cutoff}
            onChange={setCutoff}
            placeholder={t('reports:reports.backOrder.cutoffPlaceholder')}
            disabledDate={(current) => current > dayjs().endOf('day')}
          />
        </Col>
      </Row>

      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('reports:reports.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<OccLensLine>
          rowKey="id"
          dataSource={recognized}
          columns={columns}
          loading={isLoading}
          locale={{ emptyText: <ListEmptyState title={t('reports:reports.backOrder.empty')} /> }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1480 }}
        />
      </TableContainer>
      <ReportSyncNote syncedAt={syncedAt} />
    </>
  );
};

export default BackOrderReport;
